import clsx from "clsx";
import React, { useContext } from "react";
import { twMerge } from "tailwind-merge";

import Collapse from "../../components/Collapse";
import Tabs from "../../components/Tabs/Tabs";
import PrincipalBanner from "../../contents/PrincipalBanner";
import { I18nContext } from "../../i18n/useI18n";

const projectDescriptionContent = "pages.project.engineering";

const Page = () => {
  const { t } = useContext(I18nContext);
  return (
    <main className="mx-auto max-w-7xl">
      <PrincipalBanner
    
        classNames={twMerge(clsx("min-h-[200px] md:min-h-[490px] p-0"))}
      >
        <img
          className="absolute min-w-full min-h-full object-cover"
          src="https://static.igem.wiki/teams/5058/novas-imagens/banner-novo-engineering.webp"
          alt="bannner engineering"
        />
        <h1 className="invisible">{t(`${projectDescriptionContent}.title`)}</h1>
      </PrincipalBanner>

      <section className="bg-grape-light p-0 pb-4">
        <img
          className="w-full h-28 object-cover"
          src="https://static.igem.wiki/teams/5058/imgs/ondulacoes-top.svg"
          alt="border top"
        />
        <h2
          className={twMerge(
            clsx(
              "mb-7 px-8 py-3 pt-[18px] rounded-badge w-fit",
              "font-nunito font-bold lowercase text-5xl text-center text-secondary leading-9"
            )
          )}
        >
          {t(`${projectDescriptionContent}.introduction.title`)}
        </h2>
        <p className="px-6 pb-6 font-manjari text-3xl leading-normal">
          With YOB3, our goal was to build a biostimulant that can be used in
          rice farming, mitigating the demand for hazardous chemicals in
          agriculture, in such a way that it causes an increase in both plant
          resistance and productivity. For that, we aimed to engineer the yeast
          *Saccharomyces cerevisiae* to produce and export a large amount of
          Nicotinamide riboside (NR), a component of the B3 vitamin family and a
          key metabolite in the maintenance of the NAD+ pool, that has already
          been proven to elevate NAD+ levels in mammalian cells. Within the
          scope of our project, we design a biological circuit that optimizes
          not only the production of this metabolite in a low-cost growth
          medium, such as sugarcane. To facilitate the extraction of
          intracellular NAD, we built a circuit in which the cells could
          flocculate and reduce their viability after a heat shock when reaching
          the stationary phase to ensure biosecurity.
        </p>

        <img
          className="-mb-4 w-full h-28 object-cover"
          src="https://static.igem.wiki/teams/5058/imgs/ondulacoes-bottom.svg"
          alt="border top"
        />
      </section>

      <section className="py-0 pb-4">
        <h2 className="text-right ml-auto w-fit max-w-[640px] font-bold font-josefin text-7xl text-green uppercase leading-none">
          {t(`${projectDescriptionContent}.aimsanduncertainties.title`)}
        </h2>
      </section>
      <section className="py-0 pb-4">
        <h3 className="text-right mt-5 mb-1 py-3 w-full font-bold font-manjari text-5xl lowercase">
          {t(
            `${projectDescriptionContent}.aimsanduncertainties.categories.pathway.title`
          )}
        </h3>
        <p>
          {t(
            `${projectDescriptionContent}.aimsanduncertainties.categories.pathway.description`
          )}
        </p>
      </section>
      <section className="py-0 pb-4">
        <h3 className="font-bold font-manjari text-5xl text-left text-secondary lowercase leading-none">
          {t(
            `${projectDescriptionContent}.aimsanduncertainties.categories.uncertainties.title`
          )}
        </h3>
        <p>
          {t(
            `${projectDescriptionContent}.aimsanduncertainties.categories.uncertainties.description`
          )}
        </p>
        <p>
          {t(
            `${projectDescriptionContent}.aimsanduncertainties.categories.uncertainties.description2`
          )}
        </p>
        <p>
          {t(
            `${projectDescriptionContent}.aimsanduncertainties.categories.uncertainties.description3`
          )}
        </p>
        <p>
          {t(
            `${projectDescriptionContent}.aimsanduncertainties.categories.uncertainties.description4`
          )}
        </p>
        <p>
          {t(
            `${projectDescriptionContent}.aimsanduncertainties.categories.uncertainties.description5`
          )}
        </p>
      </section>

      <section className="py-0 pb-4">
        <h2 className="ml-0 pt-10 w-full max-w-none font-bold font-josefin text-7xl text-green text-left uppercase leading-none">
          {t(`${projectDescriptionContent}.genetictargets.title`)}
        </h2>

        <div className="flex flex-col justify-center w-full">
          <Tabs
            tabs={["1", "2"]}
            className={twMerge(
              clsx(
                "font-normal font-nunito text-2xl",
                "justify-center items-center align-middle",
                "p-4"
              )
            )}
          >
            <Tabs tabs={["Design", "Build", "Test", "Learn"]}>
              <article className="font-nunito text-2xl">
                <h3 className="hidden mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.design.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.design.description`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.design.description2`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="hidden mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.build.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.build.description`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.build.description2`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="hidden mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.test.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.test.description`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.test.description2`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.test.description3`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="hidden mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.learn.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.learn.description`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.learn.description2`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.learn.description3`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.learn.description4`
                  )}
                </p>
              </article>
            </Tabs>

            <Tabs tabs={["Redesign", "Build", "Test", "Learn"]}>
              <article className="font-nunito text-2xl">
                <h3 className="hidden mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.redesign.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.redesign.description`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="hidden mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.build2.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.build2.description`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="hidden mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.test2.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.test2.description`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.test2.description2`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="hidden mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.learn2.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.genetictargets.categories.learn2.description`
                  )}
                </p>
              </article>
            </Tabs>
          </Tabs>
        </div>

        <hr className="border-yellow mt-8 mb-8" />
      </section>
      <section className="py-0 pb-4">
        <h2>{t(`${projectDescriptionContent}.degron.title`)}</h2>
        <div className="flex flex-col justify-center w-full">
          <Tabs
            tabs={[]}
            className={twMerge(
              clsx(
                "font-normal font-nunito text-2xl",
                "justify-center items-center align-middle",
                "p-4"
              )
            )}
          >
            <Tabs tabs={["Design", "Build", "Test", "Learn"]}>
              <article className="font-nunito text-2xl">
                <h3 className="mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.degron.categories.design.title`
                  )}
                </h3>

                <p>
                  {t(
                    `${projectDescriptionContent}.degron.categories.design.description`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.degron.categories.design.description2`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.degron.categories.build.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.degron.categories.build.description`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.degron.categories.build.description2`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.degron.categories.test.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.degron.categories.test.description`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.degron.categories.learn.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.degron.categories.learn.description`
                  )}
                </p>
              </article>
            </Tabs>
          </Tabs>
        </div>
      </section>
      <section className="py-0 pb-4">
        <h2>{t(`${projectDescriptionContent}.nrextraction.title`)}</h2>
        <div className="flex flex-col justify-center w-full">
          <Tabs
            tabs={[]}
            className={twMerge(
              clsx(
                "font-normal font-nunito text-2xl",
                "justify-center items-center align-middle",
                "p-4"
              )
            )}
          >
            <Tabs tabs={["Design", "Build", "Test", "Learn"]}>
              <article className="font-nunito text-2xl">
                <h3 className="mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.nrextraction.categories.design.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.nrextraction.categories.design.description`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.nrextraction.categories.build.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.nrextraction.categories.build.description`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.nrextraction.categories.build.description2`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.nrextraction.categories.test.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.nrextraction.categories.test.description`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.nrextraction.categories.test.description2`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.nrextraction.categories.learn.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.nrextraction.categories.learn.description`
                  )}
                </p>
              </article>
            </Tabs>
          </Tabs>
        </div>
      </section>
      <section className="py-0 pb-4">
        <h2>{t(`${projectDescriptionContent}.plants.title`)}</h2>

        <div className="flex flex-col justify-center w-full">
          <Tabs
            tabs={[]}
            className={twMerge(
              clsx(
                "font-normal font-nunito text-2xl",
                "justify-center items-center align-middle",
                "p-4"
              )
            )}
          >
            <Tabs tabs={["Design", "Build and Test", "Learn"]}>
              <article className="font-nunito text-2xl">
                <h3 className="mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.plants.categories.design.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.plants.categories.design.description`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.plants.categories.buildtest.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.plants.categories.buildtest.description`
                  )}
                </p>
                <p>
                  {t(
                    `${projectDescriptionContent}.plants.categories.buildtest.description2`
                  )}
                </p>
              </article>
              <article className="font-nunito text-2xl">
                <h3 className="mt-5 mb-1 py-3 font-bold font-manjari text-5xl lowercase">
                  {t(
                    `${projectDescriptionContent}.plants.categories.learn.title`
                  )}
                </h3>
                <p>
                  {t(
                    `${projectDescriptionContent}.plants.categories.learn.description`
                  )}
                </p>
              </article>
            </Tabs>
          </Tabs>
        </div>
      </section>
      <section className="py-0 pb-4">
        <Collapse>
          <Collapse.Title>
            {t(`${projectDescriptionContent}.references.title`)}
          </Collapse.Title>
          <Collapse.Content>
            <p>{t(`${projectDescriptionContent}.references.text`)}</p>
            <p>{t(`${projectDescriptionContent}.references.text2`)}</p>
            <p>{t(`${projectDescriptionContent}.references.text3`)}</p>
            <p>{t(`${projectDescriptionContent}.references.text4`)}</p>
            <p>{t(`${projectDescriptionContent}.references.text5`)}</p>
            <p>{t(`${projectDescriptionContent}.references.text6`)}</p>
          </Collapse.Content>
        </Collapse>
      </section>
    </main>
  );
};
export default Page;
